import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../../lib/pocketbase';

const PricingRules = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingRule, setEditingRule] = useState(null);
    const [message, setMessage] = useState(null);

    // Initial Form State
    const initialFormState = {
        label: '',
        type: 'annee',
        condition: '',
        operator: '=',
        value: 0,
        is_percentage: true,
        priority: 0
    };

    const [form, setForm] = useState(initialFormState);

    useEffect(() => {
        fetchRules();
    }, []);

    const fetchRules = async () => {
        try {
            setLoading(true);
            const records = await pb.collection('pricing_rules').getFullList({
                sort: 'priority',
            });
            setRules(records);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erreur lors du chargement des règles.' });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setEditingRule({ id: 'new' });
        setForm(initialFormState);
    };

    const handleEditClick = (rule) => {
        setEditingRule(rule);
        setForm({
            label: rule.label,
            type: rule.type,
            condition: rule.condition,
            operator: rule.operator,
            value: rule.value,
            is_percentage: rule.is_percentage,
            priority: rule.priority
        });
    };

    const handleSubmit = async () => {
        try {
            const data = {
                ...form,
                value: parseFloat(form.value), // Ensure number
                priority: parseInt(form.priority)
            };

            if (editingRule.id === 'new') {
                await pb.collection('pricing_rules').create(data);
                setMessage({ type: 'success', text: 'Règle créée !' });
            } else {
                await pb.collection('pricing_rules').update(editingRule.id, data);
                setMessage({ type: 'success', text: 'Règle mise à jour !' });
            }

            setEditingRule(null);
            fetchRules();

            // Auto hide success message
            setTimeout(() => setMessage(null), 3000);
        } catch (err) {
            console.error(err);
            alert('Erreur: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette règle ?')) return;
        try {
            await pb.collection('pricing_rules').delete(id);
            fetchRules();
        } catch (err) {
            alert(err.message);
        }
    };

    if (loading) return <div className="admin-content-wrapper">Chargement des règles...</div>;

    return (
        <div className="admin-content-wrapper">
            <section className="intro-card admin-intro">
                <div className="admin-header-content">
                    <h2>Règles & Surcoûts</h2>
                    <p>Définissez les ajustements de prix automatiques.</p>
                </div>
                <div className="admin-header-actions">
                    <button className="primary-button" onClick={handleCreateNew}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
                        Nouvelle Règle
                    </button>
                    <button className="secondary-button" onClick={fetchRules}>Actualiser</button>
                </div>
            </section>

            {message && (
                <div className={`admin-msg ${message.type}`} style={{ marginBottom: '1rem' }}>
                    {message.text}
                </div>
            )}

            <section className="card full-width-card">
                <div className="table-responsive">
                    <table className="data-table admin-table">
                        <thead>
                            <tr>
                                <th>Priorité</th>
                                <th>Libellé Client</th>
                                <th>Condition</th>
                                <th>Ajustement</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map(rule => (
                                <tr key={rule.id}>
                                    <td>
                                        <span style={{
                                            display: 'inline-block',
                                            width: '24px',
                                            height: '24px',
                                            borderRadius: '50%',
                                            background: '#334155',
                                            color: '#f8fafc',
                                            textAlign: 'center',
                                            lineHeight: '24px',
                                            fontSize: '0.8rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {rule.priority}
                                        </span>
                                    </td>
                                    <td><strong>{rule.label}</strong></td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="service-tag cle" style={{ fontSize: '0.7rem' }}>{rule.type.toUpperCase()}</span>
                                            <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{rule.operator}</span>
                                            <span style={{ fontWeight: 'bold', color: '#f1f5f9' }}>{rule.condition}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${rule.value >= 0 ? 'completed' : 'pending'}`} style={{
                                            background: rule.value >= 0 ? 'rgba(239, 68, 68, 0.15)' : 'rgba(34, 197, 94, 0.15)',
                                            color: rule.value >= 0 ? '#f87171' : '#4ade80'
                                        }}>
                                            {rule.value > 0 ? '+' : ''}{rule.value} {rule.is_percentage ? '%' : '€'}
                                        </span>
                                    </td>
                                    <td className="text-right">
                                        <div className="action-buttons">
                                            <button
                                                className="icon-btn"
                                                title="Modifier"
                                                onClick={() => handleEditClick(rule)}
                                                style={{ color: '#3b82f6' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                            </button>
                                            <button
                                                className="icon-btn"
                                                title="Supprimer"
                                                onClick={() => handleDelete(rule.id)}
                                                style={{ color: '#ef4444' }}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rules.length === 0 && <tr><td colSpan="5" className="text-center" style={{ padding: '2rem', color: '#64748b' }}>Aucune règle définie.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* EDIT/CREATE MODAL PORTAL */}
            {editingRule && createPortal(
                <div className="modal-backdrop" onClick={() => setEditingRule(null)} style={{ zIndex: 99999 }}>
                    <div className="modal-content admin-modal-large" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                        <div className="admin-modal-header">
                            <h2>{editingRule.id === 'new' ? 'Nouvelle Règle' : 'Modifier la Règle'}</h2>
                            <button className="modal-close" onClick={() => setEditingRule(null)}>×</button>
                        </div>

                        <div className="admin-modal-body" style={{ padding: '2rem' }}>
                            <div className="pricing-grid-form">
                                <div className="form-group">
                                    <label>Libellé (Affiché au client)</label>
                                    <input
                                        className="admin-input"
                                        type="text"
                                        placeholder="ex: Surcoût BMW, Technologie 2024..."
                                        value={form.label}
                                        onChange={e => setForm({ ...form, label: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="form-row three-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label>S'applique sur</label>
                                        <select className="admin-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                                            <option value="annee">Année</option>
                                            <option value="marque">Marque</option>
                                            <option value="modele">Modèle</option>
                                            <option value="carburant">Carburant</option>
                                            <option value="vehicle_type">Type Véhicule</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Opérateur</label>
                                        <select className="admin-input" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })}>
                                            <option value="=">Est égal à</option>
                                            <option value="contains">Contient</option>
                                            <option value=">=">Supérieur ou égal</option>
                                            <option value="<=">Inférieur ou égal</option>
                                            <option value="between">Entre (X-Y)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Valeur Cible</label>
                                        <input
                                            className="admin-input"
                                            type="text"
                                            placeholder="ex: 2015, BMW"
                                            value={form.condition}
                                            onChange={e => setForm({ ...form, condition: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="form-row three-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                                    <div className="form-group">
                                        <label>Ajustement (+/-)</label>
                                        <input
                                            className="admin-input"
                                            type="number"
                                            value={form.value}
                                            onChange={e => setForm({ ...form, value: e.target.value })}
                                            placeholder="ex: 10"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Type</label>
                                        <select className="admin-input" value={form.is_percentage} onChange={e => setForm({ ...form, is_percentage: e.target.value === 'true' })}>
                                            <option value="true">Pourcentage (%)</option>
                                            <option value="false">Euros (€)</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Priorité</label>
                                        <input
                                            className="admin-input"
                                            type="number"
                                            value={form.priority}
                                            onChange={e => setForm({ ...form, priority: e.target.value })}
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                <div className="admin-msg" style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem', border: '1px solid rgba(148, 163, 184, 0.2)', padding: '0.8rem' }}>
                                    <strong style={{ color: '#facc15' }}>Aperçu :</strong> Si <strong>{form.type}</strong> {form.operator} <strong>{form.condition || '...'}</strong>, alors ajouter <strong>{form.value}{form.is_percentage ? '%' : '€'}</strong> au prix de base.
                                </div>

                            </div>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="secondary-button" onClick={() => setEditingRule(null)}>Annuler</button>
                            <button className="primary-button" onClick={handleSubmit}>{editingRule.id === 'new' ? 'Créer' : 'Sauvegarder'}</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default PricingRules;
